import classNames from 'classnames';
import { Avatar, Button, Label, Modal, TextInput, Textarea, Tooltip } from 'flowbite-react';
import React, { FormEvent, useEffect, useState } from 'react';
import { BsGithub, BsLinkedin } from 'react-icons/bs';
import { FaLinkedinIn } from 'react-icons/fa6';
import { Img } from 'react-image';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import Select, { MultiValue, SingleValue } from 'react-select';
import {
	Dropdown,
	checkDisplayUserEmail,
	companyTypeOptions,
	gradYearOptions,
	locationPreferenceOptions,
	stringAvatar,
} from 'shared-utils';
import { editProfileGradYearFn, editProfileInfoFn } from 'src/apis/profile';
import LinkedInImport from 'src/components/profile/LinkedInImport';
import ProfileUploadResume from 'src/components/profile/ProfileUploadResume';
import ProfileViewResume from 'src/components/profile/ProfileViewResume';
import UploadProfileImage from 'src/components/profile/UploadProfileImage';
import Container from 'src/components/shared/Container';
import { useToast } from 'src/context/ToastProvider';
import { useUserContext } from 'src/context/UserProvider';
import { useGetUserProfile } from 'src/hooks/profile.hooks';
import useDisclosure from 'src/hooks/utils/useDisclosure';
import { joiningOptions } from 'src/utils/item';
import { CompanyType, EditUserProfileInfo } from 'types';
import { Loader } from 'ui';
import { HiExclamation } from 'react-icons/hi';
import { RxCross2 } from 'react-icons/rx';
import { useThemeContext } from 'src/context/ThemeProvider';

const EditProfile = () => {
	const toast = useToast();
	const navigate = useNavigate();
	const { user } = useUserContext();
	const queryClient = useQueryClient();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { isOpen: isOpenLinkedInModal, onOpen: onOpenLinkedInModal, onClose: onCloseLinkedInModal } = useDisclosure();

	const [isValidSalary, setIsValidSalary] = useState<boolean>(true);
	const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
	const [userPreferencedLocation, setUserPreferencedLocation] = useState<Array<string>>([]);
	const [userCompanyTypePreference, setUserCompanyTypePreference] = useState<Array<CompanyType>>([]);
	const [isMoreThanFiveLocationSelected, setIsMoreThanFiveLocationSelected] = useState<boolean>(false);
	const [isMoreThanThreeComapnyTypeSelected, setIsMoreThanThreeComapnyTypeSelected] = useState<boolean>(false);
	const [profileUpdateInfo, setProfileUpdateInfo] = useState<EditUserProfileInfo>({});
	let locationTimeoutId: NodeJS.Timeout | undefined;
	let companyTypeTimeoutId: NodeJS.Timeout | undefined;

	const userProfileData = useGetUserProfile(user?.handler || '');

	const handleSubmitUser = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		updateUserProfileByIdFn.mutate({
			description: profileUpdateInfo.description,
			// graduationYear: profileUpdateInfo.graduationYear,
			isImmediateJoiner: profileUpdateInfo.isImmediateJoiner,
			githubURL: profileUpdateInfo.githubURL,
			linkedinURL: profileUpdateInfo.linkedinURL,
			preferredWorkLocations: userPreferencedLocation,
			preferredCompanyTypes: userCompanyTypePreference,
			currentCTC: profileUpdateInfo.currentCTC,
			currentLocation: profileUpdateInfo.currentLocation,
			desiredCTC: profileUpdateInfo.desiredCTC,
			portfolioLink: profileUpdateInfo.portfolioLink,
			workExperience: profileUpdateInfo.workExperience,
		});
	};

	const handleSubmitGradYear = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (!profileUpdateInfo.graduationYear) return;
		updateUserGradYearFn.mutate({
			gradYear: profileUpdateInfo?.graduationYear,
		});
	};

	const handleLoactionPreferences = (value: MultiValue<Dropdown>) => {
		const selectedLocation = value.map(item => item.value);
		if (selectedLocation.length <= 5) {
			setIsMoreThanFiveLocationSelected(false);
			setUserPreferencedLocation(selectedLocation);
		} else {
			setIsMoreThanFiveLocationSelected(true);
			if (locationTimeoutId) {
				clearTimeout(locationTimeoutId); // Clear the previous timeout
			}
			locationTimeoutId = setTimeout(() => {
				setIsMoreThanFiveLocationSelected(false);
			}, 3000);
		}
	};
	const handleCompanyTypePreferences = (value: MultiValue<Dropdown>) => {
		const selectedCompanyPreference = value.map(item => item.value as CompanyType);
		if (selectedCompanyPreference.length <= 3) {
			setIsMoreThanThreeComapnyTypeSelected(false);
			setUserCompanyTypePreference(selectedCompanyPreference);
		} else {
			setIsMoreThanThreeComapnyTypeSelected(true);
			if (companyTypeTimeoutId) {
				clearTimeout(companyTypeTimeoutId); // Clear the previous timeout
			}
			companyTypeTimeoutId = setTimeout(() => {
				setIsMoreThanThreeComapnyTypeSelected(false);
			}, 3000);
		}
	};

	const updateUserProfileByIdFn = useMutation({
		mutationKey: 'editUserProfileInfo',
		mutationFn: editProfileInfoFn,
		onSuccess(data) {
			queryClient.refetchQueries(['getUserProfile']);
			toast({
				title: 'Profile page edit updated successfully',
				duration: 3000,
				type: 'success',
			});

			navigate(`/profile/${userProfileData.data?.userDetail?.handler}`);
		},
	});

	const updateUserGradYearFn = useMutation({
		mutationKey: 'editProfileGradYearFn',
		mutationFn: editProfileGradYearFn,
		onSuccess(data) {
			queryClient.refetchQueries(['getUserProfile']);
			queryClient.refetchQueries(['verifyCookie']);
			toast({
				title: 'Your graduation year is updated successfully',
				duration: 3000,
				type: 'success',
			});

			navigate(`/profile/${userProfileData.data?.userDetail?.handler}`);
		},
	});

	useEffect(() => {
		if (userProfileData.data?.userDetail) {
			setProfileUpdateInfo({
				...profileUpdateInfo,
				description: userProfileData.data?.userDetail?.profile.profileDescription || '',
				graduationYear: userProfileData.data?.userDetail?.profile.gradYear,
				isImmediateJoiner: userProfileData.data?.userDetail?.profile.isImmediateJoiner ?? '',
				linkedinURL: userProfileData.data?.userDetail?.profile.linkedInProfile || '',
				githubURL: userProfileData.data?.userDetail?.profile.githubURL || '',
				portfolioLink: userProfileData.data?.userDetail.profile.portfolioLink || '',
				desiredCTC: userProfileData.data?.userDetail.profile.expectedSalary,
				currentLocation: userProfileData.data?.userDetail.profile.userLocation,
				workExperience: userProfileData.data?.userDetail.profile.totalExperience,
				currentCTC: userProfileData.data?.userDetail.profile.currentSalary,
			});
		}

		if (userProfileData.data?.userDetail && userProfileData.data?.userDetail.profile?.preferredCompanyTypes) {
			setUserCompanyTypePreference(userProfileData.data?.userDetail.profile?.preferredCompanyTypes as CompanyType[]);
		}

		if (userProfileData.data?.userDetail && userProfileData.data?.userDetail.profile?.preferredJobLocations) {
			setUserPreferencedLocation(userProfileData.data?.userDetail.profile?.preferredJobLocations);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userProfileData.data?.userDetail]);

	const { themeMode } = useThemeContext();

	const textInputStyle =
		themeMode === 'dark'
			? {
					color: 'white',
					backgroundColor: '#374151',
					border: '1px solid #4B5563',
					fontFamily: "'Inter', sans-serif",
					fontSize: '14px',
			  }
			: {};

	if (userProfileData.isLoading) return <Loader />;
	if (!userProfileData) return <div> There is nothing to edit in your Profile.</div>;

	return (
		<Container>
			<div className="w-full flex flex-col gap-6 py-4 m-auto max-w-[1024px]">
				<div className="flex flex-col gap-y-6 rounded-xl dark:bg-[#1F2A37] bg-white px-[14px] py-3 md:flex-row md:gap-3 md:p-6">
					{/* Right Side Section */}
					<div className="flex w-full flex-col gap-4 md:mt-6 md:w-[40%]">
						{/* Avatar Container */}
						<div className="relative flex items-center justify-center md:items-center">
							<Avatar
								alt="User Profile"
								placeholderInitials={stringAvatar(userProfileData.data?.userDetail?.name.trim().toUpperCase() || '')}
								rounded={true}
								img={userProfileData.data?.userDetail?.profile.proctoringImageUrl || ''}
								size={'xl'}
								bordered
								className={classNames(
									!userProfileData.data?.userDetail?.profile?.proctoringImageUrl && 'profile-avatar-div',
									'text-5xl hidden md:block',
								)}
							/>
							<Avatar
								alt="User Profile"
								placeholderInitials={stringAvatar(userProfileData.data?.userDetail?.name.trim().toUpperCase() || '')}
								rounded={true}
								img={userProfileData.data?.userDetail?.profile.proctoringImageUrl || ''}
								size={'lg'}
								bordered
								className={classNames(
									!userProfileData.data?.userDetail?.profile.proctoringImageUrl && 'profile-avatar-div',
									'text-3xl md:hidden',
								)}
							/>
							<Img
								className="absolute bottom-[-12px] left-0 right-0 mx-auto cursor-pointer max-w-[20px] max-h-[20px] md:max-w-[30px] md:max-h-[30px]"
								src={'/assets/addIcon.svg'}
								alt="addIcon"
								onClick={onOpen}
							/>
						</div>
						{/* User Name, email, phone info Container*/}
						<div className="flex w-full flex-col items-center gap-[2px]">
							<div className="flex flex-col justify-center dark:text-white">
								{/* Adding tootlip for user name */}
								{userProfileData.data?.userDetail && userProfileData.data?.userDetail?.name.length > 21 ? (
									<Tooltip content={userProfileData.data?.userDetail?.name}>
										<p className="mx-auto w-full max-w-[200px] truncate text-center font-inter text-[24px] font-[700] md:max-w-[265px]">
											{userProfileData.data?.userDetail?.name}
										</p>
									</Tooltip>
								) : (
									<p className="font-inter text-[24px] font-[700]">{userProfileData.data?.userDetail?.name}</p>
								)}
							</div>
							<div className="text-[16px] dark:text-[#d2d2d7] text-[#555770]">
								{userProfileData.data?.userDetail?.phone || ''}
							</div>
							{!!userProfileData.data?.userDetail && checkDisplayUserEmail(userProfileData.data?.userDetail) ? (
								<div className="flex flex-col justify-center text-[16px] dark:text-[#d2d2d7] text-[#555770]">
									{/* Adding tootlip for user email */}
									{userProfileData.data?.userDetail && userProfileData.data?.userDetail?.email.length > 24 ? (
										<Tooltip content={userProfileData.data?.userDetail?.email}>
											<p className="mx-auto w-full  max-w-[200px] truncate text-center font-inter text-[20px] font-[500] md:max-w-[265px]">
												{userProfileData.data?.userDetail?.email}
											</p>
										</Tooltip>
									) : (
										<p className="font-inter font-[500]">{userProfileData.data?.userDetail?.email}</p>
									)}
								</div>
							) : null}
						</div>
						{/* Upload resume button */}
						<div className="flex items-center flex-wrap gap-3 justify-center">
							<div>
								<Button
									color="purplePlaced"
									className="text-white !text-sm !font-medium font-inter !bg-[#18222D] border border-[#7777E7] hover:!bg-[#1f2a37]"
									size="md"
									onClick={() => onOpenLinkedInModal()}
								>
									<span className="bg-[#0a66c2] rounded-[1.5px] mr-[8px] flex items-center justify-center text-center">
										<FaLinkedinIn className="w-[70%] m-auto" />
									</span>
									LinkedIn Import
								</Button>
								<LinkedInImport isOpen={isOpenLinkedInModal} onClose={onCloseLinkedInModal} />
							</div>
							<div>
								{userProfileData.data?.userDetail?.profile?.resumeLink ? (
									<ProfileViewResume resumeLink={userProfileData.data?.userDetail?.profile?.resumeLink} />
								) : (
									<ProfileUploadResume />
								)}
							</div>
						</div>
					</div>
					{/* Left Side Section */}
					<div className="w-full md:w-[60%] md:px-4">
						<form className="flex flex-col gap-2 text-white md:gap-3" onSubmit={handleSubmitUser}>
							{/* Basic Details Heading */}
							<div className="border-b-2 border-[#7c85a5] pb-2">
								<p className="font-[600] leading-[24px] text-[#6698FA]">BASIC DETAILS</p>
							</div>
							{/* User Name Input */}
							<div className="mb-[8px]">
								<div className="mb-[5px]">
									<Label className="font-inter text-[14px] font-[400] dark:text-white" htmlFor="name" value="Name" />
								</div>
								<TextInput
									style={textInputStyle}
									id="name"
									name="name"
									value={userProfileData.data?.userDetail?.name || ''}
									placeholder="John Doe"
									disabled
								/>
							</div>
							{/* User Work Experience Dropdown */}
							<div className="mb-[8px]">
								<div className="mb-[5px]">
									<Label
										className="font-inter text-[14px] font-[400] dark:text-white"
										htmlFor="work_experience "
										value="Work Experience (In years)"
									/>
								</div>
								<div>
									<TextInput
										disabled={userProfileData.data?.userDetail?.profile?.totalExperience !== null ? true : false}
										style={textInputStyle}
										type="number"
										id="work_experience"
										name="work_experience"
										placeholder="0"
										min={0}
										value={profileUpdateInfo.workExperience}
										onChange={e =>
											setProfileUpdateInfo({ ...profileUpdateInfo, workExperience: Number(e.target.value) })
										}
									/>
								</div>
							</div>
							{/* User Greaduation Year Input*/}
							<div className="mb-[8px] flex justify-between items-end gap-2">
								<div className="w-full">
									<div className="mb-[5px]">
										<Label
											className="font-inter text-[14px] font-[400] dark:text-white"
											htmlFor="graduation_year "
											value="Graduation Year "
										/>
									</div>
									<div className="mt-1">
										<Select
											// isDisabled={userProfileData.data?.userDetail?.profile?.gradYear ? true : false}
											menuPortalTarget={document.body}
											classNamePrefix="my-select-plans"
											instanceId="graduation_year"
											className="single-value-select-tag"
											closeMenuOnSelect={true}
											isMulti={false}
											value={gradYearOptions.find(o => Number(o.value) === profileUpdateInfo.graduationYear)}
											options={gradYearOptions}
											onChange={(val: SingleValue<Dropdown>) => {
												setProfileUpdateInfo({
													...profileUpdateInfo,
													graduationYear: Number(val?.value),
												});
											}}
										/>
									</div>
								</div>

								<div className="">
									<Button
										color="purplePlaced"
										disabled={userProfileData.data?.userDetail?.profile?.gradYear === profileUpdateInfo?.graduationYear}
										onClick={() => setShowConfirmModal(true)}
									>
										Update
									</Button>
									<Modal size="md" show={showConfirmModal} onClose={() => setShowConfirmModal(false)} dismissible>
										<Modal.Body className="relative font-inter flex flex-col gap-y-8 justify-center rounded-[8px] border border-solid dark:border-[#555770] dark:bg-[#18222D] bg-white px-4 py-[18px]">
											<form onSubmit={handleSubmitGradYear}>
												<div className="flex items-center gap-[10px] mb-[27px]">
													<div className="flex items-center gap-[10px]">
														<HiExclamation className="h-6 w-6 text-[#ffff00]" />
														<h1 className="text-xl font-bold">Important note</h1>
													</div>
													<RxCross2
														onClick={() => setShowConfirmModal(false)}
														className="absolute right-[16px] top-[16px] h-[20px] w-[20px] cursor-pointer text-[#9CA3AF]"
													/>
												</div>

												<p className="text-center text-sm font-medium dark:text-[#E4E4EB] text-[#555770]">
													Updating your graduation year may result in your path being reassigned.
												</p>

												<p className="text-center text-sm font-medium dark:text-[#E4E4EB] text-[#555770] mt-2">
													Are you sure to update graduation year?
												</p>

												<div className="flex gap-4 text-center justify-center mt-6 pb-6">
													<Button color="purplePlaced" className="font-inter font-semibold" type="submit">
														Yes, I’m sure{' '}
													</Button>
													<Button
														color="dark"
														type="button"
														className="font-inter font-semibold"
														onClick={() => setShowConfirmModal(false)}
													>
														No, cancel
													</Button>
												</div>
											</form>
										</Modal.Body>
									</Modal>
								</div>
							</div>
							{/* User Current Location */}
							<div className="mb-[8px]">
								<div className="mb-[5px]">
									<Label
										className="font-inter text-[14px] font-[400] dark:text-white"
										htmlFor="current_location "
										value="Current Location"
									/>
								</div>
								<div>
									<Select
										// className="react-select-container"
										menuPlacement={'auto'}
										classNamePrefix="my-select-plans"
										instanceId="current_location"
										className="single-value-select-tag"
										closeMenuOnSelect={true}
										placeholder="Mumbai, Maharashtra"
										isMulti={false}
										value={
											profileUpdateInfo.currentLocation
												? {
														label: profileUpdateInfo.currentLocation,
														value: profileUpdateInfo.currentLocation,
												  }
												: null
										}
										options={locationPreferenceOptions.map(location => ({ value: location, label: location }))}
										onChange={(val: SingleValue<Dropdown>) => {
											setProfileUpdateInfo({
												...profileUpdateInfo,
												currentLocation: val?.value,
											});
										}}
									/>
								</div>
							</div>
							{/* User Profile Description */}
							<div className="mb-[8px]">
								<div className="mb-[5px]">
									<Label
										className="font-inter text-[14px] font-[400] dark:text-white"
										htmlFor="description"
										value="Description"
									/>
								</div>
								<Textarea
									style={
										themeMode === 'dark'
											? {
													color: 'white',
													backgroundColor: '#374151',
													border: '1px solid #4B5563',
													fontFamily: "'Inter', sans-serif",
													height: '100px',
													fontSize: '14px',
											  }
											: { height: '100px' }
									}
									id="description"
									name="description"
									maxLength={512}
									value={profileUpdateInfo.description || ''}
									onChange={e =>
										setProfileUpdateInfo({
											...profileUpdateInfo,
											description: e.target.value,
										})
									}
								/>
							</div>
							{/* User Portfolio/Website Link */}
							<div className="mb-[8px]">
								<div className="mb-[5px]">
									<Label
										className="font-inter text-[14px] font-[400] dark:text-white"
										htmlFor="portfolio_link "
										value="Portfolio/Website Link"
									/>
								</div>
								<div>
									<TextInput
										style={textInputStyle}
										// style={{
										// 	color: 'white',
										// 	backgroundColor: '#374151',
										// 	border: '1px solid #4B5563',
										// 	fontFamily: "'Inter', sans-serif",
										// }}
										type="url"
										id="portfolio_link"
										name="portfolio_link"
										placeholder="https://www.website.com/"
										value={profileUpdateInfo.portfolioLink || ''}
										onChange={e => setProfileUpdateInfo({ ...profileUpdateInfo, portfolioLink: e.target.value })}
									/>
								</div>
							</div>

							{/* Additional Details Heading */}
							<div className="mt-5 border-b-2 dark:border-[#7c85a5] pb-2">
								<p className="font-[600] leading-[24px] text-[#6698FA]">ADDITIONAL DETAILS</p>
							</div>
							{/* User Immediate Joining Input */}
							<div className="mb-[8px]">
								<div className="mb-[5px]">
									<Label
										className="font-inter text-[14px] font-[400] dark:text-white"
										htmlFor="immediate_joining "
										value="When will you be able to join a new job if you get a job offer? "
									/>
								</div>
								<div
									style={{
										cursor:
											typeof userProfileData.data?.userDetail?.profile?.isImmediateJoiner === 'boolean'
												? 'no-drop'
												: '',
									}}
									className="mt-1"
								>
									<Select
										isDisabled={
											typeof userProfileData.data?.userDetail?.profile?.isImmediateJoiner === 'boolean' ? true : false
										}
										menuPlacement={'auto'}
										// className="react-select-container"
										classNamePrefix="my-select-plans"
										instanceId="joining_info"
										className="single-value-select-tag"
										closeMenuOnSelect={true}
										isMulti={false}
										value={joiningOptions?.find(item => item.value === profileUpdateInfo.isImmediateJoiner)}
										options={joiningOptions}
										onChange={(val: SingleValue<Dropdown>) => {
											console.log(val);
											setProfileUpdateInfo({
												...profileUpdateInfo,
												isImmediateJoiner: val?.value ?? '',
											});
										}}
									/>
								</div>
							</div>
							{/* Location Preference Input */}
							<div className="mb-[8px]">
								<div className="mb-[5px]">
									<Label
										className="font-inter text-[14px] font-[400] dark:text-white"
										htmlFor="location_preference "
										value="What locations do you want to work in? (up to 5 locations)"
									/>
								</div>
								{isMoreThanFiveLocationSelected ? (
									<p className="font-inter text-[12px] text-red-700">You can only Select upto 5 locations</p>
								) : null}
								<div className="mt-1">
									<Select
										styles={{
											multiValue: (baseStyles, state) => ({
												...baseStyles,
												backgroundColor: '#111E2C',
											}),
										}}
										menuPlacement={'auto'}
										// className="react-select-container"
										classNamePrefix="my-select-plans"
										instanceId="location_preference"
										className="single-value-select-tag"
										isMulti={true}
										placeholder={'Select your prefered locations'}
										value={locationPreferenceOptions
											?.filter(location => userPreferencedLocation?.includes(location))
											.map(location => ({ value: location, label: location }))}
										closeMenuOnSelect={false}
										options={locationPreferenceOptions.map(location => ({ value: location, label: location }))}
										onChange={handleLoactionPreferences}
									/>
								</div>
							</div>
							{/* Company Type Preference Input */}
							<div className="mb-[8px]">
								<div className="mb-[5px]">
									<Label
										className="font-inter text-[14px] font-[400] dark:text-white"
										htmlFor="location_preference "
										value="What type of company would you prefer to work in? (up to 3 preferences)"
									/>
								</div>
								{isMoreThanThreeComapnyTypeSelected ? (
									<p className="font-inter text-[12px] text-red-700">You can only Select upto 3 Company types</p>
								) : null}
								<div className="mt-1">
									<Select
										styles={{
											multiValue: (baseStyles, state) => ({
												...baseStyles,
												backgroundColor: '#111E2C',
											}),
										}}
										menuPlacement={'auto'}
										// className="react-select-container"
										classNamePrefix="my-select-plans"
										instanceId="company_preference"
										className="single-value-select-tag"
										isMulti={true}
										closeMenuOnSelect={false}
										value={companyTypeOptions?.filter(
											companyType => userCompanyTypePreference?.includes(companyType.value as CompanyType),
										)}
										placeholder={'Select your company preferences'}
										options={companyTypeOptions}
										onChange={handleCompanyTypePreferences}
									/>
								</div>
							</div>
							{/* User Linkedin Input */}
							<div className="mb-[8px]">
								<div className="mb-[5px]">
									<Label
										className="font-inter text-[14px] font-[400] dark:text-white"
										htmlFor="linkedIn"
										value="LinkedIn"
									/>
								</div>
								<TextInput
									addon={<BsLinkedin size={'20px'} />}
									style={textInputStyle}
									type="url"
									id="linkedIn"
									name="linkedIn"
									placeholder="https://www.linkedin.com/company/levelupinchq/"
									value={profileUpdateInfo.linkedinURL || ''}
									onChange={e => {
										setProfileUpdateInfo({ ...profileUpdateInfo, linkedinURL: e.target.value });
									}}
								/>
							</div>
							{/* User Github Input */}
							<div className="mb-[8px]">
								<div className="mb-[5px]">
									<Label
										className="font-inter text-[14px] font-[400] dark:text-white"
										htmlFor="github"
										value="Github"
									/>
								</div>
								<TextInput
									addon={<BsGithub size={'20px'} />}
									style={textInputStyle}
									type="url"
									id="github"
									name="github"
									placeholder="https://github.com/leveup-inc"
									// disabled={isGithubConnected}
									value={profileUpdateInfo.githubURL || ''}
									onChange={e => {
										setProfileUpdateInfo({ ...profileUpdateInfo, githubURL: e.target.value });
									}}
								/>
							</div>
							{/* User Current CTC Input */}
							<div className="mb-[8px]">
								<div className="mb-[5px]">
									<Label
										className="font-inter text-[14px] font-[400] dark:text-white"
										htmlFor="current_ctc"
										value="What is your Current CTC (in LPA)?"
									/>
								</div>
								<TextInput
									style={textInputStyle}
									id="current_ctc"
									name="current_ctc"
									pattern={`^[0-9]{10}$`}
									type="number"
									placeholder="12"
									max={99}
									min={0}
									value={profileUpdateInfo.currentCTC || undefined}
									onChange={e => {
										e.target.value = e.target.value.replace(/[^0-9]/g, '');
										setProfileUpdateInfo({ ...profileUpdateInfo, currentCTC: Number(e.target.value) });
									}}
								/>
							</div>
							{/* User Descired CTC Input Boxes*/}
							<div className="mb-[8px]">
								<div className="mb-[5px]">
									<Label
										className="font-inter text-[14px] font-[400] dark:text-white"
										htmlFor="desired_ctc"
										value="What is your desired CTC (in LPA)?"
									/>
								</div>
								<div className="flex w-full flex-col gap-4 md:flex-row  md:gap-6">
									<TextInput
										style={textInputStyle}
										id="low_desired_ctc"
										name="desired_ctc"
										className="w-full "
										pattern={`^[0-9]{10}$`}
										type="number"
										placeholder="12"
										max={99}
										min={0}
										value={profileUpdateInfo.desiredCTC?.min || undefined}
										onChange={e => {
											e.target.value = e.target.value.replace(/[^0-9]/g, '');
											if (
												profileUpdateInfo.desiredCTC?.max &&
												Number(e.target.value) <= profileUpdateInfo.desiredCTC.max
											) {
												setIsValidSalary(true);
											} else {
												setIsValidSalary(false);
											}
											if (!profileUpdateInfo.desiredCTC && !e.target.value) setIsValidSalary(true);
											setProfileUpdateInfo({
												...profileUpdateInfo,
												desiredCTC: { min: Number(e.target.value), max: profileUpdateInfo.desiredCTC?.max || 0 },
											});
										}}
									/>

									<TextInput
										style={textInputStyle}
										id="high_desired_ctc"
										name="desired_ctc"
										className="w-full "
										pattern={`^[0-9]{10}$`}
										type="number"
										placeholder="15"
										max={99}
										min={0}
										value={profileUpdateInfo.desiredCTC?.max || undefined}
										onChange={e => {
											e.target.value = e.target.value.replace(/[^0-9]/g, '');
											if (
												profileUpdateInfo.desiredCTC?.min !== undefined &&
												Number(e.target.value) >= profileUpdateInfo.desiredCTC?.min
											) {
												setIsValidSalary(true);
											} else {
												setIsValidSalary(false);
											}
											if (!profileUpdateInfo.desiredCTC?.min && !e.target.value) setIsValidSalary(true);
											setProfileUpdateInfo({
												...profileUpdateInfo,
												desiredCTC: { max: Number(e.target.value), min: profileUpdateInfo.desiredCTC?.min || 0 },
											});
										}}
									/>
								</div>
								{!isValidSalary ? (
									<p className="pt-1 text-[11px] text-red-600">Min Salary should always be lesser than Max salary.</p>
								) : null}
							</div>
							{/* Submit Button */}
							<div className="mt-4 flex w-full gap-5">
								<Button
									color="purplePlaced"
									className="w-[87px]"
									type="submit"
									disabled={updateUserProfileByIdFn.isLoading || !isValidSalary}
								>
									{updateUserProfileByIdFn.isLoading ? <Loader /> : 'Save'}
								</Button>
								<Button
									className="w-[87px] !bg-[#555770] hover:!bg-[#44465b]"
									onClick={() => navigate(`/profile/${userProfileData.data?.userDetail?.handler}`)}
								>
									Cancel
								</Button>
							</div>
						</form>
					</div>
					<UploadProfileImage isOpen={isOpen} onClose={onClose} />
				</div>
			</div>
		</Container>
	);
};

export default EditProfile;
